import { Chip, Dialog, DialogActions, DialogContent, IconButton, Slider } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React, { useState, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import Cropper from 'react-easy-crop';
import CustomUploadButton from './CustomUploadButton';
import Image from '../common/Image';
import { ErrorToast } from '../utils/Utils';

const ASPECT_TOLERANCE = 0.05;
// Design docs state 1x display sizes; export at 3x so images stay sharp on retina screens.
const RETINA_SCALE = 3;
// TODO: bump to 300 * 1024 once the api-service/server 300KB thumbnail-size deploy is approved.
const MAX_CROP_SIZE_BYTES = 100 * 1024;
let uploadFieldIdCounter = 0;

// Preserve the uploaded file's own format (PNG keeps its alpha channel); anything else exports as JPEG.
const detectFormat = (file) => (file.type === 'image/png' ? 'png' : 'jpeg');

function getCroppedBlob(imageSrc, croppedAreaPixels, targetDimensions, options = {}) {
  const { format = 'jpeg', maxSizeBytes = null } = options;
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';

  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => {
      const outputWidth = targetDimensions?.width ?? croppedAreaPixels.width;
      const outputHeight = targetDimensions?.height ?? croppedAreaPixels.height;
      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        outputWidth,
        outputHeight,
      );

      // PNG is exported as-is, uncapped: it's lossless with no "quality" knob to shrink
      // with, and for logos/icons preserving the alpha channel at full fidelity matters
      // more than fitting an arbitrary byte cap meant for JPEG photo uploads.
      if (format === 'png' || !maxSizeBytes) {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas toBlob failed'));
              return;
            }
            resolve(blob);
          },
          mimeType,
          format === 'png' ? undefined : 0.92,
        );
        return;
      }

      const compress = (quality) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas toBlob failed'));
              return;
            }
            if (blob.size <= maxSizeBytes || quality <= 0.1) {
              resolve(blob);
            } else {
              compress(Math.max(quality - 0.1, 0.1));
            }
          },
          mimeType,
          quality,
        );
      };

      compress(0.92);
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
}

const meetsMinimumDimensions = (naturalWidth, naturalHeight, requiredDimensions) =>
  naturalWidth >= requiredDimensions.width && naturalHeight >= requiredDimensions.height;

const isExactTargetSize = (naturalWidth, naturalHeight, targetDimensions) =>
  naturalWidth === targetDimensions.width && naturalHeight === targetDimensions.height;

const isWithinAspectTolerance = (naturalWidth, naturalHeight, cropAspect) => {
  const imgRatio = naturalWidth / naturalHeight;
  return Math.abs(imgRatio - cropAspect) / cropAspect <= ASPECT_TOLERANCE;
};

export const getBestAvailableDimensions = (naturalWidth, naturalHeight, baseDimensions, maxScale = RETINA_SCALE) => {
  const scale = Math.min(naturalWidth / baseDimensions.width, naturalHeight / baseDimensions.height, maxScale);
  return {
    width: Math.round(baseDimensions.width * scale),
    height: Math.round(baseDimensions.height * scale),
  };
};

const useStyles = makeStyles({
  textfield: {
    width: ({ width }) => width || '321px',
    height: ({ height }) => height || '44px',
    padding: '12px',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '12px',
    lineHeight: '21px',
    marginTop: '12px',
    marginBottom: '12px',
    border: '1px solid rgba(228, 231, 236, 1)',
    borderRadius: '8px',
    background: 'rgba(254, 254, 254, 1)',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  btnUploadStyle: {
    height: '24px',
    marginLeft: '8px',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '15px',
    textAlign: 'center',
    letterSpacing: '0.2px',
    color: '#FFFFFF',
    background: 'rgba(98, 47, 238, 1)',
    border: '1px solid rgba(98, 47, 238, 1)',
    borderRadius: '4px',
    width: 'auto',
    padding: '0 10px',
    cursor: 'pointer',
  },
  chip: {
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    borderRadius: 0,
    background: 'rgba(242, 244, 247, 1)',
    minWidth: 0,
    maxWidth: '100%',
  },
  chipImage: {
    width: '20px',
    height: '20px',
    marginRight: '8px',
  },
  cropContainer: {
    position: 'relative',
    height: 380,
    background: '#1a1a1a',
  },
  dimensionsBadge: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    zIndex: 2,
    padding: '4px 10px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    color: '#fff',
    fontFamily: 'Montserrat',
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: '16px',
    pointerEvents: 'none',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
  },
  dialogDivider: {
    borderBottom: '1px solid #E4E7EC',
  },
  cropInstruction: {
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: '14px',
    fontWeight: '500',
    color: '#667085',
    margin: '12px 0',
  },
  dimensionWarningText: {
    fontFamily: 'Montserrat',
    fontSize: '13px',
    fontWeight: '500',
    color: '#344054',
    lineHeight: '20px',
    padding: '4px 20px 16px',
  },
  cropControls: {
    padding: '16px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cropLabel: {
    fontSize: '12px',
    fontFamily: 'Montserrat',
    fontWeight: '500',
    whiteSpace: 'nowrap',
    color: '#667085',
    lineHeight: '14px',
  },
  zoomBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#667085',
    fontSize: '22px',
    fontWeight: '500',
    padding: 0,
    lineHeight: '14px',
    height: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '4px',
    marginRight: '4px',
  },
  zoomSlider: {
    flex: 1,
    margin: '0 4px',
    color: 'rgba(98, 47, 238, 1)',
    height: 14,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    '& .MuiSlider-rail': {
      height: 6,
      borderRadius: 3,
      backgroundColor: '#E4E7EC',
      opacity: 1,
      marginTop: -3,
    },
    '& .MuiSlider-track': {
      height: 6,
      borderRadius: 3,
      marginTop: -3,
    },
    '& .MuiSlider-thumb': {
      width: 14,
      height: 14,
      marginTop: -5,
      marginLeft: -5,
      backgroundColor: '#fff',
      border: '2px solid rgba(98, 47, 238, 1)',
      boxSizing: 'border-box',
      '&:hover, &.Mui-focusVisible': {
        boxShadow: 'none',
      },
    },
  },
  dialogActions: {
    padding: '12px 20px 16px',
    gap: '8px',
    borderTop: '1px solid #E4E7EC',
  },
  cancelBtn: {
    fontFamily: 'Montserrat',
    fontWeight: '600',
    fontSize: '12px',
    color: '#555',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '6px 16px',
    cursor: 'pointer',
    background: 'white',
  },
  confirmBtn: {
    fontFamily: 'Montserrat',
    fontWeight: '600',
    fontSize: '12px',
    color: '#fff',
    background: 'rgba(98, 47, 238, 1)',
    border: '1px solid rgba(98, 47, 238, 1)',
    borderRadius: '4px',
    padding: '6px 16px',
    cursor: 'pointer',
  },
  dialogTitle: {
    fontFamily: 'Lato',
    fontSize: '14px',
    fontWeight: '700',
    color: '#10182B',
  },
  closeBtn: {
    padding: '4px',
    color: '#667085',
  },
});

const CustomUploadField = ({
  name,
  value,
  handleChange,
  handleDelete,
  fileName,
  customStyles,
  imgSrc,
  wordWrapWidth,
  accept = 'image/*',
  dimensions,
  containerStyles,
}) => {
  const classes = useStyles({ ...customStyles });
  const hasValidDimensions = Boolean(dimensions?.width > 0 && dimensions?.height > 0);
  const cropAspect = hasValidDimensions ? dimensions.width / dimensions.height : null;
  const retinaDimensions = hasValidDimensions
    ? { width: dimensions.width * RETINA_SCALE, height: dimensions.height * RETINA_SCALE }
    : null;
  const uploadInputIdRef = useRef(null);
  if (!uploadInputIdRef.current) {
    uploadFieldIdCounter += 1;
    uploadInputIdRef.current = `custom-upload-${uploadFieldIdCounter}`;
  }
  const uploadInputId = uploadInputIdRef.current;
  const [cropOpen, setCropOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropTargetDimensions, setCropTargetDimensions] = useState(null);
  const [dimensionWarningOpen, setDimensionWarningOpen] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const originalFileRef = useRef(null);
  const originalFileFormatRef = useRef('jpeg');

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const proceedWithImage = (file, naturalWidth, naturalHeight, targetDimensions) => {
    const withinTolerance = isWithinAspectTolerance(naturalWidth, naturalHeight, cropAspect);
    const exactSize = isExactTargetSize(naturalWidth, naturalHeight, targetDimensions);

    if (withinTolerance && exactSize) {
      handleChange({ target: { files: [file], name } });
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawImageSrc(ev.target.result);
      setCropTargetDimensions(targetDimensions);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelected = (e) => {
    if (!cropAspect) {
      handleChange(e);
      return;
    }

    const file = e.target.files[0];
    if (!file) return;
    originalFileRef.current = file;
    originalFileFormatRef.current = detectFormat(file);

    const objectUrl = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { naturalWidth, naturalHeight } = img;

      if (!meetsMinimumDimensions(naturalWidth, naturalHeight, retinaDimensions)) {
        setPendingImage({ file, naturalWidth, naturalHeight });
        setDimensionWarningOpen(true);
        return;
      }

      proceedWithImage(file, naturalWidth, naturalHeight, retinaDimensions);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      originalFileRef.current = null;
      ErrorToast('Failed to load image');
    };
    img.src = objectUrl;
    // reset so same file can trigger onChange again if user cancels and re-selects
    e.target.value = '';
  };

  const handleDimensionWarningCancel = () => {
    setDimensionWarningOpen(false);
    setPendingImage(null);
    originalFileRef.current = null;
  };

  const handleDimensionWarningConfirm = () => {
    if (!pendingImage) return;
    const { file, naturalWidth, naturalHeight } = pendingImage;
    setDimensionWarningOpen(false);
    setPendingImage(null);
    const scaledDimensions = getBestAvailableDimensions(naturalWidth, naturalHeight, dimensions);
    proceedWithImage(file, naturalWidth, naturalHeight, scaledDimensions);
  };

  const handleCropConfirm = async () => {
    if (!croppedAreaPixels || !originalFileRef.current) return;

    try {
      const format = originalFileFormatRef.current;
      const blob = await getCroppedBlob(rawImageSrc, croppedAreaPixels, cropTargetDimensions, {
        format,
        maxSizeBytes: MAX_CROP_SIZE_BYTES,
      });
      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
      const croppedFile = new File([blob], originalFileRef.current.name, { type: mimeType });
      handleChange({ target: { files: [croppedFile], name } });
      setCropOpen(false);
      setRawImageSrc(null);
      setCroppedAreaPixels(null);
      setCropTargetDimensions(null);
    } catch (error) {
      ErrorToast('Failed to process image');
    }
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    setRawImageSrc(null);
    setCroppedAreaPixels(null);
    setCropTargetDimensions(null);
    originalFileRef.current = null;
  };

  return (
    <>
      <div className={classes.textfield} style={{ ...containerStyles }}>
        <Chip
          className={classes.chip}
          onDelete={imgSrc || value ? handleDelete : undefined}
          deleteIcon={imgSrc || value ? undefined : <span>&times;</span>}
          label={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                ...(fileName ? { flex: 1 } : {}),
                justifyContent: 'flex-start',
              }}
            >
              {typeof imgSrc === 'string' && imgSrc ? (
                <Image alt="" src={imgSrc} className={classes.chipImage} />
              ) : (
                value && <img src={value} alt="" className={classes.chipImage} />
              )}
              {fileName && (
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    ...(wordWrapWidth && { maxWidth: wordWrapWidth }),
                    flex: 1,
                  }}
                >
                  {fileName}
                </span>
              )}
            </div>
          }
        />

        <CustomUploadButton
          id={uploadInputId}
          name={name}
          disableElevation
          handleImageUpload={handleFileSelected}
          className={classes.btnUploadStyle}
          styles={{ height: '30px', border: '1px solid rgba(228, 231, 236, 1)', background: 'white' }}
          accept={accept}
        />
      </div>

      {hasValidDimensions && (
        <Dialog open={cropOpen} onClose={handleCropCancel} maxWidth="sm" fullWidth>
          <div className={classes.dialogHeader}>
            <span className={classes.dialogTitle}>Crop Image</span>
            <IconButton className={classes.closeBtn} size="small" onClick={handleCropCancel} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.dialogDivider} />

          <DialogContent style={{ padding: 0 }}>
            <p className={classes.cropInstruction}>Drag image to reposition</p>
            <div className={classes.cropContainer}>
              {rawImageSrc && (
                <Cropper
                  image={rawImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={cropAspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
              {hasValidDimensions && (
                <span className={classes.dimensionsBadge}>
                  {dimensions.width} x {dimensions.height}
                </span>
              )}
            </div>
            <div className={classes.cropControls}>
              <span className={classes.cropLabel}>Zoom Out</span>
              <button
                type="button"
                className={classes.zoomBtn}
                onClick={() => setZoom((prev) => Math.max(1, prev - 0.1))}
                aria-label="Zoom out"
              >
                &minus;
              </button>
              <Slider
                className={classes.zoomSlider}
                value={zoom}
                min={1}
                max={3}
                step={0.05}
                onChange={(_, val) => setZoom(val)}
              />
              <button
                type="button"
                className={classes.zoomBtn}
                onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}
                aria-label="Zoom in"
              >
                +
              </button>
              <span className={classes.cropLabel}>Zoom In</span>
            </div>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <button type="button" className={classes.cancelBtn} onClick={handleCropCancel}>
              Cancel
            </button>
            <button type="button" className={classes.confirmBtn} onClick={handleCropConfirm}>
              Upload
            </button>
          </DialogActions>
        </Dialog>
      )}

      {hasValidDimensions && (
        <Dialog open={dimensionWarningOpen} onClose={handleDimensionWarningCancel} maxWidth="sm" fullWidth>
          <div className={classes.dialogHeader}>
            <span className={classes.dialogTitle}>Image smaller than recommended</span>
            <IconButton
              className={classes.closeBtn}
              size="small"
              onClick={handleDimensionWarningCancel}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.dialogDivider} />
          <DialogContent>
            <p className={classes.dimensionWarningText}>
              This image is smaller than the recommended size ({retinaDimensions?.width} x {retinaDimensions?.height}
              px) &mdash; yours is {pendingImage?.naturalWidth} x {pendingImage?.naturalHeight}px. It may look blurry on
              high-resolution screens.
            </p>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <button type="button" className={classes.cancelBtn} onClick={handleDimensionWarningCancel}>
              Cancel
            </button>
            <button type="button" className={classes.confirmBtn} onClick={handleDimensionWarningConfirm}>
              Upload Anyway
            </button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default React.memo(CustomUploadField);
